import React, { useRef, useState } from "react";
import { UserView, UserViewType } from "../../types/user";
import { useUserRegistry } from "../../hooks/useUserRegistry";
import StbButton from "../security-toolbox-ui/StbButton";
import { HbrTab } from "@harbor/elements-react-standalone";


const MAX_STRING_LENGTH = 30;
const BUTTON_VISIBILITY_DELAY = 800;

interface UserViewNavigationItemProps {
  view: UserView;
  viewType: UserViewType;
  handleDeleteView: (viewId: UserView['id']) => void;
  selectedView?: UserView | null;
  setSelectedView: (view: UserView) => void;
}

const UserViewNavigationItem: React.FC<UserViewNavigationItemProps> = ({
  view,
  viewType,
  handleDeleteView,
  selectedView,
  setSelectedView
}) => {
  const [editingViewId, setEditingViewId] = useState<UserView['id'] | null>(null);
  const [newViewName, setNewViewName] = useState('');
  const [hoveredViewId, setHoveredViewId] = useState<number | null>(null);
  const visibilityDelayRef = useRef<NodeJS.Timeout | null>(null);

  const { 
    updateUserView,
  } = useUserRegistry();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setNewViewName(e.target.value);
  };

  const handleCancelEdit = () => {
    setEditingViewId(null);
    setNewViewName('');
  };

  const handleSaveEdit = async () => {
    if (editingViewId !== null) {
      const updatedView = {...view, name: newViewName };
      await updateUserView(viewType, updatedView);
      setEditingViewId(null);
      setNewViewName('');
    }
  }

  const handleEditClick = (
    e: React.MouseEvent<HTMLElement>,
    id: number
  ) => {
    e.stopPropagation();
    if (view) {
      setEditingViewId(id);
      setNewViewName(view.name);
    }
  };

  const handleInputKeyStroke = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key || e.code;
    if ([' ', 'Specebar', 'Space', 'ArrowLeft', 'ArrowRight', 'Enter', 'Escape'].includes(key)) {
      e.stopPropagation();
    }
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleMouseEnter = (id: number) => {
    visibilityDelayRef.current = setTimeout(() => {
      setHoveredViewId(id);
    }, BUTTON_VISIBILITY_DELAY);
  };

const handleMouseLeave = () => {
  if (visibilityDelayRef.current) {
    clearTimeout(visibilityDelayRef.current);
    visibilityDelayRef.current = null;
  }
  setHoveredViewId(null);
};

  return (
    <HbrTab
      slot="nav"
      onHbr-click={(e) => {
        if (!editingViewId) {
          setSelectedView(view)
        }
      }}
      onClick={(e) => {
        if (editingViewId) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
      className="view-navigation"
      active={selectedView?.id === view.id}
    >
      <div
        onMouseEnter={() => handleMouseEnter(view.id)}
        onMouseLeave={handleMouseLeave}
        className="flex flex-row items-center"
      >
        {editingViewId === view.id 
          ? (
              <input
                value={newViewName}
                onChange={handleInputChange}
                onBlur={handleCancelEdit}
                onKeyDownCapture={(e) => handleInputKeyStroke(e)}
                autoFocus
                className="p-1 w-full min-w-[15rem]"
                maxLength={MAX_STRING_LENGTH}
              />
            ) 
          : (
              <>
                <div slot="label">{view.name}</div>

                <div
                  slot="suffix"
                  className={`transition-opacity duration-300 ${hoveredViewId === view.id && editingViewId !== view.id ? 'opacity-100 ml-2' : 'opacity-0'}`}
                >
                  {hoveredViewId === view.id && editingViewId !== view.id && (
                    <>
                      <StbButton
                        role="update"
                        iconOnly
                        variant="text"
                        size="small"
                        onClick={(e) => handleEditClick(e, view.id)}
                        className="mr-1"
                      />
                      <StbButton
                        role="delete"
                        iconOnly
                        variant="text"
                        size="small"
                        onClick={() => handleDeleteView(view.id)}
                      />
                    </>
                  )}
                </div>
              </>
            )
        }
      </div>
    </HbrTab>
  )
}

export default UserViewNavigationItem;